/**
 * S9 · From subscription to ready content (#how) — THE FEED homepage,
 * design.md Part III; FINAL-SPEC §4 S9 ("Edit timeline", M3).
 *
 *  - ≥900 with motion: the section is pinned (CSS, 220svh + a sticky stage).
 *    The scroll driver writes --p on the section; here we derive the active
 *    clip (data-active = min(3, floor(p·3)+1), written only on change), mark
 *    clips active / rendered, and tick the monitor's timecode only when its
 *    whole second changes. --nav-h (the sticky top) is measured on resize.
 *  - <900 with motion: as each clip rises in, the playhead steps down the
 *    rail to that clip's badge and the rendered rail fills behind it.
 *  - Reduced motion / no-JS: nothing here runs; the list is complete.
 */
import { onReveal, registerScene, reduced } from "./runtime";

const PIN_MIN = 900;
const TOTAL_SECONDS = 15;

export function init(): void {
  const section = document.getElementById("how");
  if (!section) return;
  if (!document.documentElement.classList.contains("js-motion")) return;
  if (reduced()) return;

  const clips = Array.from(section.querySelectorAll<HTMLElement>(".tl-clip"));
  const lane = section.querySelector<HTMLElement>(".tl-lane");
  const timeline = section.querySelector<HTMLElement>(".tl");
  const tcNow = section.querySelector<HTMLElement>(".how-mon__tc-now");
  const clipNo = section.querySelector<HTMLElement>(".how-mon__clipno");
  const wide = window.matchMedia(`(min-width: ${PIN_MIN}px)`);

  /* ── the sticky top: the measured nav height (zoomed at ≥2000, so never
     hard-coded) ── */
  const measureNav = (): void => {
    let h = 0;
    for (const sel of [".site-nav", ".story"]) {
      const el = document.querySelector<HTMLElement>(sel);
      if (!el) continue;
      const pos = getComputedStyle(el).position;
      if (pos === "sticky" || pos === "fixed")
        h = Math.max(h, el.getBoundingClientRect().height);
    }
    section.style.setProperty("--nav-h", `${Math.round(h || 64)}px`);
  };

  /* ── M3 (≥900): playhead → active clip + timecode ── */
  let active = 0;
  let second = -1;
  const setActive = (n: number): void => {
    if (n === active) return;
    active = n;
    section.setAttribute("data-active", String(n));
    clips.forEach((c, i) => {
      c.classList.toggle("is-active", i + 1 === n);
      c.classList.toggle("is-done", i + 1 < n);
    });
    if (clipNo) clipNo.textContent = `0${n}/03`;
  };
  const onProgress = (p: number): void => {
    setActive(Math.min(3, Math.floor(p * 3) + 1));
    const s = Math.min(TOTAL_SECONDS, Math.floor(p * TOTAL_SECONDS + 1e-6));
    if (s !== second && tcNow) {
      second = s;
      tcNow.textContent = `00:${String(s).padStart(2, "0")}`;
    }
  };
  const syncMode = (): void => {
    const pin = wide.matches;
    section.classList.toggle("is-pin", pin);
    if (pin) {
      measureNav();
      if (active === 0) setActive(1);
    }
  };
  syncMode();
  wide.addEventListener("change", syncMode);
  window.addEventListener("resize", measureNav, { passive: true });
  registerScene(section, "pin", { minWidth: PIN_MIN, onProgress });

  /* While pinned, the track sits at the bottom of a sticky stage — inside the
     shared reveal IO's bottom margin — so the clips get their own trigger: they
     rise once a quarter of the timeline is on screen. */
  if (timeline && "IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting) || !wide.matches) return;
        for (const c of clips) c.classList.add("in");
        io.disconnect();
      },
      { threshold: 0.25 },
    );
    io.observe(timeline);
  }

  /* ── <900: the playhead steps down to each clip's badge as it enters ── */
  if (!lane) return;
  let reached = -1;
  const stepTo = (index: number): void => {
    if (wide.matches || index <= reached) return;
    reached = index;
    const c = clips[index];
    const badge = c?.querySelector<HTMLElement>(".tl-clip__num");
    if (!c || !badge) return;
    // local CSS px (no zoom below 900): clip offset within the track + badge centre
    const y = c.offsetTop + badge.offsetTop + badge.offsetHeight / 2;
    // the rail runs from 24px to the LAST badge's centre, so a full fill
    // lands exactly on step 3
    const railTop = 24;
    const last = clips[clips.length - 1];
    const lastBadge = last?.querySelector<HTMLElement>(".tl-clip__num");
    const lastY =
      last && lastBadge
        ? last.offsetTop + lastBadge.offsetTop + lastBadge.offsetHeight / 2
        : lane.offsetHeight - railTop;
    lane.style.setProperty(
      "--rail-end",
      `${Math.max(0, Math.round(lane.offsetHeight - lastY))}px`,
    );
    lane.style.setProperty("--ph-y", `${Math.round(y)}px`);
    lane.style.setProperty(
      "--fill",
      Math.min(
        1,
        Math.max(0, (y - railTop) / Math.max(1, lastY - railTop)),
      ).toFixed(3),
    );
  };
  onReveal((el) => {
    const i = clips.indexOf(el as HTMLElement);
    if (i >= 0) stepTo(i);
  });
}
